import * as XLSX from 'xlsx';

/* Flat fallback — used when only a raw status is available */
const STATUS_LABEL = {
  present:            'Full Day',
  late:               'Late',
  'short-leave':      'Short Leave',
  'half-day':         'Half Day',
  'missing-punch':    'Missing Punch',
  leave:              'Leave',
  'half-leave':       'Half Day Leave',
  holiday:            'Holiday',
  'no-record':        'No Record',
};

/* Reason-refined labels — used when status + reason are both present */
const STATUS_REASON_LABEL = {
  'short-leave|morning':  'Morning Short Leave',
  'short-leave|evening':  'Evening Short Leave',
  'half-day|morning':     'Morning Half Day',
  'half-day|evening':     'Evening Half Day',
  'half-day|early-out':   'Evening Half Day',
};

function resolveStatusLabel(r) {
  if (r.StatusLabel) return r.StatusLabel;

  const combo = STATUS_REASON_LABEL[`${r.Status}|${r.StatusReason}`];
  if (combo) return combo;

  return STATUS_LABEL[r.Status] || r.Status || '';
}

function pad2(n) { return String(n).padStart(2, '0'); }
function stamp(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* Raw decimal hours */
const formatHours = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Number(n.toFixed(2))} h`;
};

export function exportAttendanceDetailsToExcel({ rows, filters }) {
  const activeFilters = Object.entries(filters || {})
    .filter(([, v]) => v && v !== 'All')
    .map(([k, v]) => `${k}: ${v}`)
    .join('   |   ') || 'None';

  const headerRows = [
    ['ATTENDANCE DETAILS'],
    [`Filters: ${activeFilters}`],
    [`Generated: ${stamp()}`],
    [`Records: ${rows.length}`],
    [],
  ];

  const columns = [
    { key: 'Date',   label: 'Date',   width: 12 },
    { key: 'Day',    label: 'Day',    width: 10 },
    { key: 'Status', label: 'Status', width: 20, format: resolveStatusLabel },
    { key: 'InTime', label: 'In Time',  width: 10 },
    { key: 'OutTime', label: 'Out Time', width: 10 },
    { key: 'TotalHours',       label: 'Total Hours',        width: 14, time: true },
    { key: 'NormalHours',      label: 'Normal Hours',       width: 14, time: true },
    { key: 'OTHours',          label: 'OT Hours',           width: 12, time: true },
    { key: 'OTHoursRate',      label: 'OT Hours Rate',      width: 14, numeric: true },
    { key: 'AdjustedOTHours',  label: 'Adjusted OT Hours',  width: 18, time: true },
  ];

  const columnHeader = columns.map((c) => c.label);

  const bodyRows = rows.map((r) =>
    columns.map((c) => {
      if (c.format) return c.format(r);

      const raw = r[c.key];

      if (c.time)    return formatHours(raw);
      if (c.numeric) return num(raw);

      return raw === null || raw === undefined ? '' : raw;
    })
  );

  const totalsRow = columns.map((c) => {
    if (c.key === 'Date')
      return `TOTAL — ${rows.length} record${rows.length === 1 ? '' : 's'}`;

    if (c.time) {
      const total = rows.reduce((s, r) => s + num(r[c.key]), 0);
      return formatHours(total);
    }

    return '';
  });

  const aoa = [...headerRows, columnHeader, ...bodyRows, [], totalsRow];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws['!cols'] = columns.map((c) => ({ wch: c.width || 12 }));
  const lastCol = columns.length - 1;
  ws['!merges'] = headerRows.slice(0, 4).map((_, i) => ({
    s: { r: i, c: 0 }, e: { r: i, c: lastCol },
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance Details');

  const d = new Date();
  const filename = `Attendance_Details_${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}